import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FormFunnel } from "components/form-funnel";
import { FunnelConfig } from "components/form-funnel/types";
import { verifyKiosk, submitKiosk } from "services/onboarding.service";
import { step2Steps } from "containers/my-onboarding/onboarding-configs";
import { isEmployeeFormLocked } from "utils/onboarding-errors";
import useIsTouchScreen from "components/form-funnel/use-is-touch-screen";

const KIOSK_SESSION_KEY = "onboarding_kiosk_session";

function OnboardingKiosk() {
  const { t, i18n } = useTranslation();
  const lang = ((i18n as any).language || "en") as "lt" | "en";

  const [personalEmail, setPersonalEmail] = useState("");
  const [kioskCode, setKioskCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [kioskData, setKioskData] = useState<any>(null);
  const [isComplete, setIsComplete] = useState(false);
  // 44 px targets and 16 px text on the office tablet; a mouse keeps the compact size.
  const controlSize = useIsTouchScreen() ? "py-3 text-base" : "py-2 text-sm";

  // Restore session on mount
  useEffect(() => {
    const saved = sessionStorage.getItem(KIOSK_SESSION_KEY);
    if (saved) {
      try {
        const { email, code } = JSON.parse(saved);
        if (email && code) {
          setPersonalEmail(email);
          setKioskCode(code);
          // Auto-verify with saved credentials
          setIsVerifying(true);
          verifyKiosk({ personalEmail: email, kioskCode: code })
            .then((data) => {
              setKioskData(data.onboarding || data);
            })
            .catch(() => {
              // Credentials no longer valid — clear session
              sessionStorage.removeItem(KIOSK_SESSION_KEY);
            })
            .finally(() => setIsVerifying(false));
        }
      } catch {
        sessionStorage.removeItem(KIOSK_SESSION_KEY);
      }
    }
  }, []);

  // Background refresh every 5s when logged in but no pending actions
  const hasForm = kioskData && kioskData.status === "step_2";
  useEffect(() => {
    if (!kioskData || hasForm || isComplete) return;
    const interval = setInterval(() => {
      const saved = sessionStorage.getItem(KIOSK_SESSION_KEY);
      if (!saved) return;
      try {
        const { email, code } = JSON.parse(saved);
        verifyKiosk({ personalEmail: email, kioskCode: code })
          .then((data) => setKioskData(data.onboarding || data))
          .catch(() => {});
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [kioskData, hasForm, isComplete]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!personalEmail.trim() || !kioskCode.trim()) {
      setError(t("fill_all_fields"));
      return;
    }
    setError(null);
    setIsVerifying(true);
    try {
      const data = await verifyKiosk({
        personalEmail: personalEmail.trim(),
        kioskCode: kioskCode.trim(),
      });
      const onboarding = data.onboarding || data;
      setKioskData(onboarding);
      // Save credentials to sessionStorage for refresh persistence
      sessionStorage.setItem(
        KIOSK_SESSION_KEY,
        JSON.stringify({ email: personalEmail.trim(), code: kioskCode.trim() })
      );
    } catch (e: any) {
      if (e?.response?.status === 429) {
        setError(t("rate_limit_exceeded"));
      } else {
        setError(t("invalid_code"));
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // The form was already submitted (in the employee's own account, on another
  // kiosk), so the server refuses changes: say so and show the submitted
  // state instead of the form. False for any other failure.
  const showFormLockedIf = (e: unknown): boolean => {
    if (!isEmployeeFormLocked(e)) return false;
    setError(t("employee_form_locked"));
    setKioskData((prev: any) => ({ ...prev, employeeSubmitted: true }));
    return true;
  };

  // Determine which steps to show based on kiosk data
  const getStepsConfig = (): FunnelConfig | null => {
    if (!kioskData) return null;
    const status = kioskData.status;

    const allSteps = [
      ...(status === "step_2" ? step2Steps : []),
    ];

    // For kiosk, remove file upload steps (handled by company rep)
    const filteredSteps = allSteps.filter((s) => s.id !== "documents");

    if (filteredSteps.length === 0) return null;

    return {
      id: "kiosk-onboarding",
      autoSave: false,
      steps: filteredSteps,
      initialData: {
        ...kioskData,
        hasConflictOfInterest: kioskData.hasConflictOfInterest === true ? "yes" : kioskData.hasConflictOfInterest === false ? "no" : kioskData.hasConflictOfInterest,
        gdprConsent1BirthdayGreeting: kioskData.gdprConsent1BirthdayGreeting ?? false,
        gdprConsent2PhotoSessions: kioskData.gdprConsent2PhotoSessions ?? false,
        gdprConsent3Website: kioskData.gdprConsent3Website ?? false,
        gdprConsent4SocialMedia: kioskData.gdprConsent4SocialMedia ?? false,
        gdprConsent5ClientPresentations: kioskData.gdprConsent5ClientPresentations ?? false,
        gdprConsent6InternalPhotoVideo: kioskData.gdprConsent6InternalPhotoVideo ?? false,
        gdprConsent7InternalNetwork: kioskData.gdprConsent7InternalNetwork ?? false,
      },
      onSave: async (stepId, data) => {
        try {
          await submitKiosk({
            ...data,
            personalEmail: personalEmail.trim(),
            kioskCode: kioskCode.trim(),
            stepId,
          });
        } catch (e) {
          if (!showFormLockedIf(e)) throw e;
        }
      },
      onSubmit: async (data) => {
        try {
          await submitKiosk({
            ...data,
            personalEmail: personalEmail.trim(),
            kioskCode: kioskCode.trim(),
            final: true,
          });
        } catch (e) {
          if (showFormLockedIf(e)) return;
          throw e;
        }
        // Re-fetch to get updated employeeSubmitted flag
        const refreshed = await verifyKiosk({
          personalEmail: personalEmail.trim(),
          kioskCode: kioskCode.trim(),
        });
        setKioskData(refreshed.onboarding || refreshed);
        setIsComplete(true);
      },
    };
  };

  // Loading state (auto-verify from sessionStorage)
  if (isVerifying && !kioskData) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-gray-50 p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Login screen
  if (!kioskData && !isComplete) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <div className="flex flex-col gap-4">
              <h1 className="text-xl font-semibold text-center">
                {t("onboarding")}
              </h1>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg">
                  {error}
                </div>
              )}

              <form className="space-y-4" onSubmit={handleVerify}>
                <div>
                  <label htmlFor="kiosk-email" className="block text-sm font-medium text-gray-700 mb-1">
                    {t("personal_email")}
                  </label>
                  <input
                    id="kiosk-email"
                    type="email"
                    className={`w-full px-3 ${controlSize} border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    value={personalEmail}
                    onChange={(e) => setPersonalEmail(e.target.value)}
                    placeholder="vardas@email.lt"
                  />
                </div>
                <div>
                  <label htmlFor="kiosk-code" className="block text-sm font-medium text-gray-700 mb-1">
                    {t("kiosk_code")}
                  </label>
                  <input
                    id="kiosk-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className={`w-full px-3 ${controlSize} border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    value={kioskCode}
                    onChange={(e) => setKioskCode(e.target.value)}
                    placeholder="000000"
                  />
                </div>
                <button
                  type="submit"
                  className={`w-full px-4 ${controlSize} bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50`}
                  disabled={isVerifying}
                >
                  {isVerifying ? "..." : t("login")}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success screen
  if (isComplete) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <div className="flex flex-col gap-3">
              <h1 className="text-xl font-semibold text-center">
                {t("thank_you")}
              </h1>
              <p className="text-center">
                {t("kiosk_complete_message")}
              </p>
              <button
                type="button"
                className={`w-full px-4 ${controlSize} bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium`}
                onClick={() => {
                  setKioskData(null);
                  setIsComplete(false);
                  setPersonalEmail("");
                  setKioskCode("");
                }}
              >
                {t("start_over")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Form filling screen
  const funnelConfig = getStepsConfig();
  const isStepDataSubmitted = kioskData?.employeeSubmitted;

  return (
    <div className="min-h-dvh bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-xl font-semibold">
                {t("onboarding")}
              </h1>
              <p className="text-gray-500">
                {kioskData?.firstName} {kioskData?.lastName}
              </p>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg">
                {error}
              </div>
            )}
            {kioskData?.status === "work_security" ? (
              <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 rounded-lg">
                {t("kiosk_work_security_message")}
              </div>
            ) : funnelConfig && !isStepDataSubmitted ? (
              <FormFunnel config={funnelConfig} />
            ) : isStepDataSubmitted ? (
              <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-lg">
                {t("data_submitted_waiting")}
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 rounded-lg">
                {t("kiosk_no_pending_actions")}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OnboardingKiosk;
